import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchIngresos, deleteIngreso } from '../actions/ingresoActions';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import IngresoForm from './IngresoForm';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Pagination, Table, Button } from 'react-bootstrap';
import CustomPrintModal from './CustomPrintModal';
import moment from 'moment';
import 'moment/locale/es';

moment.locale('es');

const IngresosList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { ingresos, error } = useSelector(state => state.ingreso);
    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [content, setContent] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'desc' });

    useEffect(() => {
        dispatch(fetchIngresos());
    }, [dispatch]);

    const sortedIngresos = useMemo(() => {
        const sorted = [...ingresos];
        sorted.sort((a, b) => {
            let valueA = a[sortConfig.key];
            let valueB = b[sortConfig.key];

            if (sortConfig.key === 'fecha') {
                valueA = valueA ? new Date(valueA).getTime() : 0;
                valueB = valueB ? new Date(valueB).getTime() : 0;
            } else {
                valueA = (valueA || '').toString().toLowerCase();
                valueB = (valueB || '').toString().toLowerCase();
            }

            if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [ingresos, sortConfig]);

    const requestSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setCurrentPage(1);
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return 'bi-arrow-down-up';
        return sortConfig.direction === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
    };

    const handleDelete = (nrorec) => {
        confirmAlert({
            title: 'Confirmar eliminacion',
            message: 'Estas seguro de eliminar este ingreso y sus detalles?',
            buttons: [
                {
                    label: 'Si',
                    onClick: () => {
                        dispatch(deleteIngreso(nrorec))
                            .then(() => {
                                toast.success('Ingreso eliminado');
                                dispatch(fetchIngresos());
                            })
                            .catch(error => toast.error(`Error: ${error.message}`));
                    }
                },
                { label: 'No' }
            ]
        });
    };

    const handlePrintModalShow = () => {
        const printContent = {
            head: [['Nro', 'Recibo', 'Beneficiario', 'Concepto', 'Fecha', 'Monto Total']],
            body: sortedIngresos.map((ingreso, index) => [
                index + 1,
                ingreso.nrorec,
                ingreso.beneficiario,
                ingreso.concepto,
                moment(ingreso.fecha).format('DD/MM/YYYY'),
                `$${Number(ingreso.monto).toFixed(2)}`
            ])
        };
        setContent(printContent);
        setShowPrintModal(true);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentIngresos = sortedIngresos.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedIngresos.length / itemsPerPage);

    if (error) toast.error(`Error: ${error}`);

    return (
        <div className="list2-container container-list">
            <h2>Listado de Ingresos</h2>
            
            <div className="row mt-3">
                <div className="col-lg-8">
                    <label>Registros por pagina:</label>
                    <select value={itemsPerPage} disabled>
                        <option value="10">10</option>
                    </select>
                </div>
                <div className="col-lg-3 float-lg-end">
                    <Button type="button" variant="accent" onClick={() => {
                        setIsEdit(false);
                        setSelectedId(null);
                        setShowForm(true);
                    }}>
                        <i className="bi bi-plus-lg"></i> Nuevo Ingreso
                    </Button>
                </div>
            </div>

            <div className="table-container">
                <Table hover striped>
                    <thead>
                        <tr>
                            <th>Nro</th>
                            <th role="button" onClick={() => requestSort('nrorec')}>Recibo <i className={`bi ${getSortIcon('nrorec')}`}></i></th>
                            <th role="button" onClick={() => requestSort('beneficiario')}>Beneficiario <i className={`bi ${getSortIcon('beneficiario')}`}></i></th>
                            <th role="button" onClick={() => requestSort('concepto')}>Concepto <i className={`bi ${getSortIcon('concepto')}`}></i></th>
                            <th role="button" onClick={() => requestSort('fecha')}>Fecha <i className={`bi ${getSortIcon('fecha')}`}></i></th>
                            <th>Monto Total</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentIngresos.map((ingreso, index) => (
                            <tr key={ingreso.nrorec}>
                                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                <td>{ingreso.nrorec}</td>
                                <td>{ingreso.beneficiario}</td>
                                <td>{ingreso.concepto}</td>
                                <td>{moment(ingreso.fecha).format('DD/MM/YYYY')}</td>
                                <td>${Number(ingreso.monto).toFixed(2)}</td>
                                <td>
                                    <Button type="button" variant="outline-accent" size="sm" onClick={() => {
                                        setSelectedId(ingreso.nrorec);
                                        setIsEdit(true);
                                        setShowForm(true);
                                    }}>
                                        <i className="bi bi-pencil"></i>
                                    </Button>
                                    <Button type="button" variant="outline-danger" size="sm" onClick={() => handleDelete(ingreso.nrorec)}>
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <div className="button-group separate">
                <Button type="button" variant="secondary" onClick={() => navigate('/')}>
                    <i className="bi bi-arrow-left"></i> Volver
                </Button>
                <Button type="button" variant="accent" onClick={handlePrintModalShow}>
                    <i className="bi bi-printer"></i> Imprimir
                </Button>
            </div>

            <Pagination className="mt-3">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
                {[...Array(totalPages)].map((_, idx) => (
                    <Pagination.Item key={idx + 1} active={idx + 1 === currentPage} onClick={() => setCurrentPage(idx + 1)}>
                        {idx + 1}
                    </Pagination.Item>
                ))}
                <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>

            {showForm && (
                <IngresoForm 
                    show={showForm} 
                    handleClose={() => {
                        setShowForm(false);
                        dispatch(fetchIngresos());
                    }} 
                    isEdit={isEdit} 
                    ingresoId={selectedId} 
                />
            )}

            <CustomPrintModal 
                show={showPrintModal} 
                handleClose={() => setShowPrintModal(false)} 
                title="LISTADO DE INGRESOS" 
                content={content} 
            />
        </div>
    );
};

export default IngresosList;
